// src/lib/invalidation.ts
import { useQueryClient, Query } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';

/**
 * ワークスペース単位で一覧系を一括無効化。
 * history は boardId を含む queryKey が複数存在しうるため、
 * predicate で「['history','list', wsId, ...]」前方一致を厳密に判定します。
 */
export const useInvalidation = () => {
  const queryClient = useQueryClient();

  const invalidateWorkspace = (wsId: string) => {
    const normalizedWsId = String(wsId || '').trim();
    if (!normalizedWsId) {
      if (import.meta.env.DEV) {
        console.warn('[invalidation] skip invalidateWorkspace: missing wsId');
      }
      return;
    }
    // files / boards / tags のワークスペース関連クエリを全て無効化
    // 動的キーに対応するため predicate を使用
    queryClient.invalidateQueries({
      predicate: (q: Query) => {
        const k = q.queryKey as unknown as Array<unknown>;
        return Array.isArray(k) && k[0] === 'files' && k[1] === 'list' && k[2] === normalizedWsId;
      },
    });
    
    queryClient.invalidateQueries({
      predicate: (q: Query) => {
        const k = q.queryKey as unknown as Array<unknown>;
        return Array.isArray(k) && k[0] === 'boards' && k[1] === 'list' && k[2] === normalizedWsId;
      },
    });

    queryClient.invalidateQueries({
      predicate: (q: Query) => {
        const k = q.queryKey as unknown as Array<unknown>;
        return Array.isArray(k) && k[0] === 'threads' && k[2] === normalizedWsId;
      },
    });
    
    queryClient.invalidateQueries({ queryKey: queryKeys.tags.list(normalizedWsId) });

    // history は boardId 付きのキー（['history','list', wsId, boardId]）が多数ある想定
    queryClient.invalidateQueries({
      predicate: (q: Query) => {
        const k = q.queryKey as unknown as Array<unknown>;
        return Array.isArray(k) && k[0] === 'history' && k[1] === 'list' && k[2] === normalizedWsId;
      },
    });

    // calendar データも無効化
    queryClient.invalidateQueries({ queryKey: queryKeys.calendar.data(normalizedWsId) });
  };

  return { invalidateWorkspace };
};
